import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import type {
  BulkUpdateListingsPayload,
  ListModerationHistoryParams,
  UpdateListingPricePayload,
  UpdateReportStatusPayload,
} from "./client";
import { moderationApi } from "./client";

export const moderationKeys = {
  all: ["moderation"] as const,
  listings: (q: Partial<OffsetPageQueryDTO> & { q?: string }) =>
    ["moderation", "listings", q] as const,
  listingProducts: (q: Partial<OffsetPageQueryDTO> & { q?: string }) =>
    ["moderation", "listing-products", q] as const,
  reports: (q: Partial<OffsetPageQueryDTO>) =>
    ["moderation", "reports", q] as const,
  history: (q: Partial<ListModerationHistoryParams>) =>
    ["moderation", "history", q] as const,
};

export function useListingProductsQuery(
  query: Partial<OffsetPageQueryDTO> & { q?: string; recentDays?: number } = {},
) {
  const params = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    q: query.q,
    recentDays: query.recentDays,
  };
  return useQuery({
    queryKey: moderationKeys.listingProducts(params),
    queryFn: () => moderationApi.listListingProducts(params),
  });
}

export function useListingProductQuery(catalogProductId: string) {
  return useQuery({
    queryKey: ["moderation", "listing-product", catalogProductId] as const,
    queryFn: () => moderationApi.getListingProduct(catalogProductId),
    enabled: Boolean(catalogProductId),
  });
}

export function useListingsQuery(
  query: Partial<OffsetPageQueryDTO> & { q?: string } = {},
) {
  const params = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    q: query.q,
  };
  return useQuery({
    queryKey: moderationKeys.listings(params),
    queryFn: () => moderationApi.listListings(params),
  });
}

export function useListingQuery(id: string) {
  return useQuery({
    queryKey: ["moderation", "listing", id] as const,
    queryFn: () => moderationApi.getListing(id),
    enabled: !!id,
  });
}

export function useBulkUpdateListingsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkUpdateListingsPayload) =>
      moderationApi.bulkUpdateListingsStatus(payload),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: moderationKeys.all });
      toast.success(`ອັບເດດສຳເລັດ ${data.updated} ລາຍການ`);
    },
    onError: () => {
      toast.error("ອັບເດດລາຍການລົ້ມເຫຼວ");
    },
  });
}

export function useUpdateListingPriceMutation(listingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateListingPricePayload) =>
      moderationApi.updateListingPrice(listingId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: moderationKeys.all });
      qc.invalidateQueries({ queryKey: ["moderation", "listing", listingId] });
      toast.success("ອັບເດດລາຄາ Marketplace ສຳເລັດ");
    },
    onError: () => {
      toast.error("ອັບເດດລາຄາລົ້ມເຫຼວ");
    },
  });
}

export function useReportsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const params = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: moderationKeys.reports(params),
    queryFn: () => moderationApi.listReports(params),
  });
}

export function useReportQuery(id: string) {
  return useQuery({
    queryKey: ["moderation", "report", id] as const,
    queryFn: () => moderationApi.getReport(id),
    enabled: !!id,
  });
}

export function useUpdateReportStatusMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateReportStatusPayload) =>
      moderationApi.updateReportStatus(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: moderationKeys.all });
      qc.invalidateQueries({ queryKey: ["moderation", "report", id] });
      toast.success("ດຳເນີນການສຳເລັດ");
    },
    onError: () => {
      toast.error("ດຳເນີນການລົ້ມເຫຼວ");
    },
  });
}

export function useModerationHistoryQuery(
  params: Partial<ListModerationHistoryParams> = {},
) {
  const queryParams = {
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
    fromDate: params.fromDate,
    toDate: params.toDate,
    targetType: params.targetType,
  };
  return useQuery({
    queryKey: moderationKeys.history(queryParams),
    queryFn: () => moderationApi.listModerationHistory(queryParams),
  });
}
